(() => {
  const canonicalTerms = {
    vinyl: ['vinyl', 'vinil', 'vynil', 'vnly', 'vinylboden', 'designvinyl', 'designboden', 'designbelag'],
    laminat: ['laminat', 'laminnat', 'lamminat', 'laminatboden'],
    parkett: ['parkett', 'parket', 'parquet', 'holzboden', 'echtholzboden'],
    fischgrat: ['fischgrat', 'fischgrät', 'fischgraet', 'herringbone'],
    klickvinyl: ['klickvinyl', 'clickvinyl', 'klick vinyl', 'click vinyl'],
    klebevinyl: ['klebevinyl', 'dryback', 'dry back', 'klebe vinyl'],
    sockelleiste: ['sockelleiste', 'sockelleisten', 'fußleiste', 'fussleiste', 'fußleisten', 'fussleisten'],
    trittschall: ['trittschall', 'trittschalldämmung', 'trittschalldaemmung', 'unterlagsmatte'],
  };

  const categoryExpansions = {
    vinyl: ['vinyl', 'designboden', 'designbelag'],
    klickvinyl: ['klickvinyl', 'klick vinyl', 'click vinyl'],
    klebevinyl: ['klebevinyl', 'klebe vinyl', 'dryback'],
    laminat: ['laminat'],
    parkett: ['parkett', 'holzboden', 'echtholz'],
    fischgrat: ['fischgrät', 'fischgrat', 'herringbone'],
  };

  const colorExpansions = {
    dunkel: ['dunkel', 'dark', 'schwarz', 'black', 'anthrazit', 'mud', 'smoked'],
    hell: ['hell', 'light', 'weiß', 'weiss', 'white', 'beige', 'natural'],
    grau: ['grau', 'grey', 'gray', 'beton', 'concrete', 'slate'],
    braun: ['braun', 'brown', 'oak', 'eiche', 'nussbaum', 'walnut'],
  };

  const normalize = (value) => value
    .toLocaleLowerCase('de-DE')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9äöüß\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const distance = (left, right) => {
    const rows = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let i = 1; i <= left.length; i += 1) {
      let previous = rows[0];
      rows[0] = i;
      for (let j = 1; j <= right.length; j += 1) {
        const current = rows[j];
        rows[j] = Math.min(rows[j] + 1, rows[j - 1] + 1, previous + (left[i - 1] === right[j - 1] ? 0 : 1));
        previous = current;
      }
    }
    return rows[right.length];
  };

  const identifyTerm = (word) => {
    for (const [canonical, variants] of Object.entries(canonicalTerms)) {
      if (variants.some((variant) => normalize(variant) === word)) return canonical;
    }

    if (word.length < 4) return word;

    let closest = word;
    let closestDistance = 3;
    for (const [canonical, variants] of Object.entries(canonicalTerms)) {
      for (const variant of variants) {
        const normalizedVariant = normalize(variant);
        const currentDistance = distance(word, normalizedVariant);
        if (currentDistance < closestDistance) {
          closest = canonical;
          closestDistance = currentDistance;
        }
      }
    }
    return closest;
  };

  const buildQueries = (term) => {
    const normalized = normalize(term);
    const words = normalized.split(' ').filter(Boolean).map(identifyTerm);
    const category = Object.keys(categoryExpansions).find((key) => words.includes(key));
    const color = Object.keys(colorExpansions).find((key) => words.includes(key));
    const remaining = words.filter((word) => word !== category && word !== color);
    const queries = [];
    const add = (query) => {
      const cleanQuery = query.filter(Boolean).join(' ').trim();
      if (cleanQuery && !queries.includes(cleanQuery)) queries.push(cleanQuery);
    };

    add(words);

    const categories = category ? categoryExpansions[category] : [''];
    const colors = color ? colorExpansions[color] : [''];

    categories.slice(0, 3).forEach((categoryTerm) => {
      add([categoryTerm, color || '', ...remaining]);
    });

    if (color) {
      colors.slice(1, 5).forEach((colorTerm) => {
        add([categories[0], colorTerm, ...remaining]);
      });
    }

    if (category === 'vinyl') add(['designboden', ...remaining]);

    return queries.slice(0, 7);
  };

  const extractMarkup = (text) => new DOMParser()
    .parseFromString(text, 'text/html')
    .querySelector('#shopify-section-predictive-search');

  const mergeResults = (sections, originalTerm) => {
    const usableSections = sections.filter(Boolean);
    if (!usableSections.length) return '';

    const score = (section) => section.querySelectorAll('#predictive-search-results-products-list > li').length;
    const baseSection = usableSections.reduce((best, section) => score(section) > score(best) ? section : best);
    const baseList = baseSection.querySelector('#predictive-search-results-products-list');

    if (baseList) {
      const knownUrls = new Set(Array.from(baseList.querySelectorAll('a[href]')).map((link) => link.getAttribute('href')));
      for (const section of usableSections) {
        for (const item of section.querySelectorAll('#predictive-search-results-products-list > li')) {
          const url = item.querySelector('a[href]')?.getAttribute('href');
          if (!url || knownUrls.has(url) || baseList.children.length >= 10) continue;
          knownUrls.add(url);
          baseList.append(item.cloneNode(true));
        }
      }

      Array.from(baseList.children).forEach((item, index) => {
        item.id = `predictive-search-option-product-${index + 1}`;
      });
    }

    const termNode = baseSection.querySelector('[data-predictive-search-search-for-text]');
    if (termNode) termNode.textContent = originalTerm;

    const totalNode = baseSection.querySelector('[data-total-results]');
    if (totalNode && baseList) totalNode.dataset.totalResults = String(baseList.children.length);

    return baseSection.innerHTML;
  };

  customElements.whenDefined('predictive-search').then(() => {
    const PredictiveSearchElement = customElements.get('predictive-search');

    PredictiveSearchElement.prototype.getSearchResults = function getSearchResults(searchTerm) {
      const queryKey = normalize(searchTerm).replace(/\s+/g, '-');
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      this.bpRequestNumber = (this.bpRequestNumber || 0) + 1;
      const requestNumber = this.bpRequestNumber;
      const queries = buildQueries(searchTerm);

      Promise.all(queries.map((query) => fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=10&section_id=predictive-search`,
        { signal: this.abortController.signal },
      ).then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })))
        .then((responses) => {
          if (requestNumber !== this.bpRequestNumber) return;
          const markup = mergeResults(responses.map(extractMarkup), searchTerm);
          if (!markup) {
            this.close();
            return;
          }
          this.allPredictiveSearchInstances.forEach((instance) => {
            instance.cachedResults[queryKey] = markup;
          });
          this.renderSearchResults(markup);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || error?.code === 20) return;
          this.close();
        });
    };
  });
})();
